import type { UIElement } from "@json-render/core";
import type { AppComponentName, AppViewSpec } from "./catalog";

/**
 * A reference App view that uses every component in the catalog: a supplier
 * scorecard. It is the gallery page's content, the renderer's test fixture,
 * and an example an authoring agent can be shown.
 */

function el(
  type: AppComponentName,
  props: Record<string, unknown>,
  children?: string[],
  on?: UIElement["on"],
): UIElement {
  return {
    type,
    props,
    ...(children ? { children } : {}),
    ...(on ? { on } : {}),
  };
}

export const appGalleryState: Record<string, unknown> = {
  tab: "overview",
  supplier: {
    name: "Northline Supply Co.",
    id: "SUP-00412",
    terms: "Net 30",
    since: "2019-04-01",
    status: "Active",
    spend: 1284500.5,
  },
  invoices: [
    {
      id: "INV-88121",
      date: "2026-09-28",
      status: "Paid",
      amount: 18420.12,
      variance: 0,
    },
    {
      id: "INV-88140",
      date: "2026-10-02",
      status: "Pending",
      amount: 9310,
      variance: 0.021,
    },
    {
      id: "INV-88177",
      date: "2026-10-06",
      status: "Exception",
      amount: 22105.4,
      variance: 0.084,
    },
    {
      id: "INV-88190",
      date: "2026-10-09",
      status: "Draft",
      amount: 4120.75,
      variance: null,
    },
  ],
  monthlySpend: [
    { month: "2026-05-01", spend: 98200, category: "Packaging" },
    { month: "2026-06-01", spend: 104500, category: "Packaging" },
    { month: "2026-07-01", spend: 112300, category: "Packaging" },
    { month: "2026-08-01", spend: 109800, category: "Packaging" },
    { month: "2026-09-01", spend: 121400, category: "Packaging" },
    { month: "2026-05-01", spend: 41200, category: "Freight" },
    { month: "2026-06-01", spend: 39800, category: "Freight" },
    { month: "2026-07-01", spend: 45100, category: "Freight" },
    { month: "2026-08-01", spend: 47600, category: "Freight" },
    { month: "2026-09-01", spend: 44300, category: "Freight" },
  ],
  spendByCategory: [
    { category: "Packaging", spend: 546200 },
    { category: "Freight", spend: 218000 },
    { category: "Raw materials", spend: 391500 },
    { category: "Maintenance", spend: 128800 },
  ],
  skuMargins: [
    { sku: "PK-100", cost: 4.2, margin: 0.31, units: 1200 },
    { sku: "PK-220", cost: 7.9, margin: 0.24, units: 640 },
    { sku: "FR-014", cost: 12.5, margin: 0.18, units: 310 },
    { sku: "RM-880", cost: 2.1, margin: 0.42, units: 2300 },
    { sku: "MT-031", cost: 19.4, margin: 0.12, units: 90 },
  ],
  form: { reason: "", category: "", notes: "", notify: true, autoFix: false },
  dialogs: { reject: false, remove: false },
  panels: { detail: false },
  reject: { reason: "" },
};

const elements: Record<string, UIElement> = {
  root: el("Stack", { gap: "md" }, ["header", "tabs"]),
  header: el("Inline", { justify: "between" }, ["headerText", "headerActions"]),
  headerText: el("Stack", { gap: "xs" }, ["eyebrow", "title"]),
  eyebrow: el("Text", { text: "Supplier scorecard", variant: "eyebrow" }),
  title: el("Heading", { text: { $state: "/supplier/name" }, level: 2 }),
  headerActions: el("Inline", {}, [
    "status",
    "portalLink",
    "exportButton",
    "flagButton",
  ]),
  status: el("Badge", {
    text: { $state: "/supplier/status" },
    tone: "success",
    icon: "check-circle",
  }),
  portalLink: el("Link", {
    text: "Supplier portal",
    href: "https://example.com/portal",
  }),
  exportButton: el("Button", {
    label: "Export",
    variant: "outline",
    icon: "download-simple",
  }),
  flagButton: el(
    "Button",
    { label: "Flag for review", variant: "default", icon: "flag" },
    undefined,
    {
      press: {
        action: "setState",
        params: { statePath: "/tab", value: "review" },
      },
    },
  ),

  tabs: el(
    "Tabs",
    {
      value: { $bindState: "/tab" },
      tabs: [
        { value: "overview", label: "Overview" },
        { value: "invoices", label: "Invoices" },
        { value: "charts", label: "Charts" },
        { value: "review", label: "Review" },
        { value: "overlays", label: "Overlays" },
        { value: "states", label: "States" },
      ],
    },
    [
      "overviewTab",
      "invoicesTab",
      "chartsTab",
      "reviewTab",
      "overlaysTab",
      "statesTab",
    ],
  ),

  overviewTab: el("Tab", { value: "overview" }, [
    "agentNote",
    "metricsSection",
    "detailsSep",
    "detailsSection",
    "locationsSection",
  ]),
  agentNote: el("AgentNote", {
    title: "Agent summary",
    text: "Price variance on INV-88177 is 8.4%, above the 5% tolerance. Two of the last four invoices matched cleanly. Recommend holding INV-88177 until the PO is revised.",
  }),
  metricsSection: el("Section", { title: "Last 90 days" }, [
    "metricsGrid",
    "onTime",
    "tolerance",
    "invoicesLink",
  ]),
  metricsGrid: el("Grid", { columns: 4 }, [
    "mSpend",
    "mInvoices",
    "mMatch",
    "mDays",
  ]),
  mSpend: el("Metric", {
    label: "Spend",
    value: { $state: "/supplier/spend" },
    format: "currency",
    delta: 0.042,
    deltaLabel: "vs prior 90 days",
    positiveIsGood: false,
  }),
  mInvoices: el("Metric", {
    label: "Invoices",
    value: 148,
    delta: 0.11,
    deltaLabel: "vs prior 90 days",
  }),
  mMatch: el("Metric", {
    label: "Clean match rate",
    value: 0.913,
    format: "percent",
    delta: -0.018,
    deltaLabel: "vs prior 90 days",
  }),
  mDays: el("Metric", {
    label: "Avg days to pay",
    value: 27.4,
    delta: -0.06,
    deltaLabel: "vs prior 90 days",
    positiveIsGood: false,
  }),
  onTime: el("Progress", { label: "On-time delivery", value: 86 }),
  tolerance: el("Inline", { gap: "xs", align: "center" }, [
    "toleranceIcon",
    "toleranceText",
  ]),
  toleranceIcon: el("Icon", { name: "warning", tone: "warning" }),
  toleranceText: el("Text", {
    text: "1 invoice is over the 5% price tolerance.",
    variant: "muted",
  }),
  invoicesLink: el("Link", {
    text: "Open all invoices",
    href: "/accounts-payable/invoices",
  }),
  detailsSep: el("Separator", {}),
  detailsSection: el(
    "Section",
    { title: "Details", description: "From the supplier master." },
    ["detailsGrid"],
  ),
  detailsGrid: el("Grid", { columns: 4 }, ["fId", "fTerms", "fSince", "fRep"]),
  fId: el("Field", {
    label: "Supplier ID",
    value: { $state: "/supplier/id" },
    format: "mono",
  }),
  fTerms: el("Field", {
    label: "Payment terms",
    value: { $state: "/supplier/terms" },
  }),
  fSince: el("Field", {
    label: "Supplier since",
    value: { $state: "/supplier/since" },
    format: "date",
  }),
  fRep: el("Field", { label: "Account rep", value: null }),
  locationsSection: el("Section", { title: "Ship-from locations" }, [
    "locationsGrid",
  ]),
  locationsGrid: el("Grid", { columns: 2 }, ["chicago", "dallas"]),
  chicago: el("Card", { title: "Chicago DC" }, ["chicagoFields"]),
  chicagoFields: el("Grid", { columns: 2 }, ["chicagoLead", "chicagoFill"]),
  chicagoLead: el("Field", { label: "Lead time", value: "4 days" }),
  chicagoFill: el("Field", {
    label: "Fill rate",
    value: 0.97,
    format: "percent",
  }),
  dallas: el("Card", { title: "Dallas DC" }, ["dallasFields"]),
  dallasFields: el("Grid", { columns: 2 }, ["dallasLead", "dallasFill"]),
  dallasLead: el("Field", { label: "Lead time", value: "6 days" }),
  dallasFill: el("Field", {
    label: "Fill rate",
    value: 0.91,
    format: "percent",
  }),

  invoicesTab: el("Tab", { value: "invoices" }, [
    "invoiceTable",
    "activitySep",
    "activitySection",
  ]),
  invoiceTable: el(
    "Table",
    {
      columns: [
        {
          key: "id",
          label: "Invoice",
          format: "mono",
          href: "/accounts-payable/invoices?invoiceNumber={id}",
        },
        { key: "date", label: "Date", format: "date" },
        { key: "status", label: "Status", format: "badge" },
        { key: "variance", label: "Price variance", format: "percent" },
        { key: "amount", label: "Amount", format: "currency" },
      ],
      rows: { $state: "/invoices" },
      rowActions: [{ value: "review", label: "Flag for review" }],
    },
    undefined,
    {
      rowPress: {
        action: "setState",
        params: { statePath: "/tab", value: "review" },
      },
    },
  ),
  activitySep: el("Separator", {}),
  activitySection: el("Section", { title: "Recent activity" }, ["activity"]),
  activity: el("List", {
    items: [
      {
        title: "INV-88177 flagged",
        description: "Price variance 8.4% on line 3.",
        meta: "Oct 6",
        badge: "Exception",
      },
      {
        title: "Payment sent",
        description: "INV-88121 · ACH",
        meta: "Oct 1",
        badge: "Paid",
      },
      {
        title: "Terms updated",
        description: "Net 45 → Net 30",
        meta: "Sep 12",
      },
    ],
  }),

  chartsTab: el("Tab", { value: "charts" }, [
    "spendSection",
    "mixSection",
    "customSection",
  ]),
  spendSection: el(
    "Section",
    { title: "Spend", description: "Monthly, by category." },
    ["spendGrid"],
  ),
  spendGrid: el("Grid", { columns: 2 }, ["spendLine", "spendBars"]),
  spendLine: el("LineChart", {
    data: { $state: "/monthlySpend" },
    x: "month",
    y: "spend",
    color: "category",
    point: true,
    valueFormat: "currency",
    title: "Monthly spend",
  }),
  spendBars: el("BarChart", {
    data: { $state: "/spendByCategory" },
    x: "category",
    y: "spend",
    horizontal: true,
    valueFormat: "currency",
    valueLabels: true,
    title: "Spend by category",
  }),
  mixSection: el("Section", { title: "Mix and margin" }, ["mixGrid"]),
  mixGrid: el("Grid", { columns: 3 }, ["mixPie", "mixArea", "mixScatter"]),
  mixPie: el("PieChart", {
    data: { $state: "/spendByCategory" },
    category: "category",
    value: "spend",
    donut: true,
    valueFormat: "currency",
    title: "Category share",
  }),
  mixArea: el("AreaChart", {
    data: { $state: "/monthlySpend" },
    x: "month",
    y: "spend",
    color: "category",
    stack: true,
    title: "Stacked spend",
  }),
  mixScatter: el("ScatterChart", {
    data: { $state: "/skuMargins" },
    x: "cost",
    y: "margin",
    size: "units",
    yFormat: "percent",
    title: "Cost vs. margin",
  }),
  customSection: el(
    "Section",
    {
      title: "Custom chart",
      description:
        "A hand-written Vega-Lite spec, for what the standard charts can't draw.",
    },
    ["customChart"],
  ),
  customChart: el("Chart", {
    grammar: "vega-lite",
    data: { $state: "/skuMargins" },
    title: "Margin by SKU, with the average",
    height: 240,
    spec: {
      layer: [
        {
          mark: "bar",
          encoding: {
            x: { field: "sku", type: "nominal", sort: "-y" },
            y: {
              field: "margin",
              type: "quantitative",
              axis: { format: ".0%" },
            },
          },
        },
        {
          mark: { type: "rule", strokeDash: [4, 4] },
          encoding: { y: { aggregate: "mean", field: "margin" } },
        },
      ],
    },
  }),

  reviewTab: el("Tab", { value: "review" }, ["reviewSection"]),
  reviewSection: el(
    "Section",
    { title: "Flag for review", description: "Sends a task to the AP team." },
    ["reviewGrid", "notes", "notify", "reviewSep", "autoFix", "reviewActions"],
  ),
  reviewGrid: el("Grid", { columns: 2 }, ["reason", "category"]),
  reason: el("TextInput", {
    label: "Reason",
    value: { $bindState: "/form/reason" },
    placeholder: "Price variance above tolerance",
  }),
  category: el("Select", {
    label: "Category",
    value: { $bindState: "/form/category" },
    placeholder: "Choose a category",
    options: [
      { value: "pricing", label: "Pricing" },
      { value: "quality", label: "Quality" },
      { value: "delivery", label: "Delivery" },
    ],
  }),
  notes: el("TextArea", {
    label: "Notes",
    value: { $bindState: "/form/notes" },
    rows: 3,
  }),
  notify: el("Checkbox", {
    label: "Notify the supplier's account rep",
    checked: { $bindState: "/form/notify" },
  }),
  reviewSep: el("Separator", {}),
  autoFix: el("Switch", {
    label:
      "Automatically fix errors and keep the App up to date with the latest Gently tools",
    description:
      "Repairs that succeed repeatedly are applied to this App without asking.",
    checked: { $bindState: "/form/autoFix" },
  }),
  reviewActions: el("Inline", { justify: "end" }, ["cancel", "submit"]),
  cancel: el("Button", { label: "Cancel", variant: "ghost" }, undefined, {
    press: {
      action: "setState",
      params: { statePath: "/tab", value: "overview" },
    },
  }),
  submit: el("Button", { label: "Create task", variant: "default" }),

  overlaysTab: el("Tab", { value: "overlays" }, [
    "overlayActions",
    "overlayDialog",
    "overlayConfirm",
    "overlaySheet",
    "splitSection",
  ]),
  overlayActions: el("Inline", { gap: "sm" }, [
    "popover",
    "openReject",
    "openRemove",
    "openDetail",
    "tooltip",
    "hoverCard",
  ]),
  popover: el("Popover", { title: "Payment terms", align: "start" }, [
    "popoverTrigger",
    "popoverTerms",
    "popoverSince",
  ]),
  popoverTrigger: el("Button", { label: "Terms", variant: "outline" }),
  popoverTerms: el("Field", {
    label: "Terms",
    value: { $state: "/supplier/terms" },
  }),
  popoverSince: el("Field", {
    label: "Supplier since",
    value: { $state: "/supplier/since" },
    format: "date",
  }),
  openReject: el(
    "Button",
    { label: "Reject invoice", variant: "outline" },
    undefined,
    {
      press: {
        action: "setState",
        params: { statePath: "/dialogs/reject", value: true },
      },
    },
  ),
  openRemove: el(
    "Button",
    { label: "Remove supplier", variant: "ghost" },
    undefined,
    {
      press: {
        action: "setState",
        params: { statePath: "/dialogs/remove", value: true },
      },
    },
  ),
  openDetail: el(
    "Button",
    { label: "Invoice detail", variant: "ghost" },
    undefined,
    {
      press: {
        action: "setState",
        params: { statePath: "/panels/detail", value: true },
      },
    },
  ),
  tooltip: el("Tooltip", { content: "Opens in the supplier master" }, [
    "tooltipTrigger",
  ]),
  tooltipTrigger: el("Button", { label: "Master record", variant: "ghost" }),
  hoverCard: el("HoverCard", { align: "start" }, [
    "hoverTrigger",
    "hoverStatus",
    "hoverSpend",
  ]),
  hoverTrigger: el("Text", { text: "SUP-00412", variant: "label" }),
  hoverStatus: el("Field", {
    label: "Status",
    value: { $state: "/supplier/status" },
    format: "badge",
  }),
  hoverSpend: el("Field", {
    label: "Spend",
    value: { $state: "/supplier/spend" },
    format: "currency",
  }),
  overlayDialog: el(
    "Dialog",
    {
      title: "Reject invoice",
      description: "The supplier is notified with your reason.",
      open: { $bindState: "/dialogs/reject" },
    },
    ["rejectReason", "rejectFooter"],
  ),
  rejectReason: el("TextArea", {
    label: "Rejection reason",
    value: { $bindState: "/reject/reason" },
    rows: 3,
  }),
  rejectFooter: el("OverlayFooter", {}, ["rejectCancel", "rejectSubmit"]),
  rejectCancel: el("Button", { label: "Cancel", variant: "ghost" }, undefined, {
    press: {
      action: "setState",
      params: { statePath: "/dialogs/reject", value: false },
    },
  }),
  rejectSubmit: el(
    "Button",
    { label: "Reject", variant: "default" },
    undefined,
    {
      press: [
        { action: "reject", params: { reason: { $state: "/reject/reason" } } },
        {
          action: "setState",
          params: { statePath: "/dialogs/reject", value: false },
        },
      ],
    },
  ),
  overlayConfirm: el(
    "ConfirmDialog",
    {
      title: "Remove this supplier?",
      description: "Open invoices keep their history. This cannot be undone.",
      confirmLabel: "Remove",
      tone: "destructive",
      open: { $bindState: "/dialogs/remove" },
    },
    undefined,
    { confirm: { action: "removeSupplier" } },
  ),
  overlaySheet: el(
    "Sheet",
    {
      title: "Invoice INV-88177",
      description: "Exception: price variance above tolerance.",
      resizable: true,
      open: { $bindState: "/panels/detail" },
    },
    ["sheetFields", "sheetFooter"],
  ),
  sheetFields: el("Grid", { columns: 2 }, ["sheetAmount", "sheetVariance"]),
  sheetAmount: el("Field", {
    label: "Amount",
    value: 22105.4,
    format: "currency",
  }),
  sheetVariance: el("Field", {
    label: "Price variance",
    value: 0.084,
    format: "percent",
  }),
  sheetFooter: el("OverlayFooter", {}, ["sheetClose"]),
  sheetClose: el("Button", { label: "Close", variant: "outline" }, undefined, {
    press: {
      action: "setState",
      params: { statePath: "/panels/detail", value: false },
    },
  }),
  splitSection: el(
    "Section",
    {
      title: "Review side by side",
      description: "Drag the divider to resize.",
    },
    ["split"],
  ),
  split: el("ResizablePanels", { direction: "horizontal", height: 240 }, [
    "splitDocument",
    "splitForm",
  ]),
  splitDocument: el("ResizablePanel", { defaultSize: 55, minSize: 25 }, [
    "splitDocumentText",
  ]),
  splitDocumentText: el("Text", {
    text: "Invoice INV-88177. 40 units at $552.64, net 30.",
    variant: "muted",
  }),
  splitForm: el("ResizablePanel", { defaultSize: 45, minSize: 25 }, [
    "splitInput",
  ]),
  splitInput: el("TextInput", {
    label: "Corrected total",
    value: { $bindState: "/form/correctedTotal" },
  }),

  statesTab: el("Tab", { value: "states" }, [
    "loadingSection",
    "loadError",
    "empty",
  ]),
  loadingSection: el("Section", { title: "Payment history" }, ["skeleton"]),
  skeleton: el("Skeleton", { lines: 3 }),
  loadError: el("Alert", {
    title: "Couldn't load contract terms",
    description: "The supplier master did not respond. Try again in a minute.",
    tone: "destructive",
  }),
  empty: el("EmptyState", {
    title: "No open exceptions",
    description: "Invoices that fail matching show up here.",
    actionLabel: "View all invoices",
  }),
};

export const appGallerySpec: AppViewSpec = {
  root: "root",
  elements,
  state: appGalleryState,
};
